/**
 * Cookie consent library — CNIL / RGPD compliant.
 *
 * Design goals:
 * - Non-essential cookies are OFF by default.
 * - Consent is stored in localStorage (key: gate33_cookie_consent).
 * - A change fires a `gate33:consent-updated` window event so the app
 *   can (un)load third-party scripts accordingly.
 *
 * Storage format:
 *   { version: 1, timestamp: ISO, choices: { essential: true, analytics: bool, marketing: bool } }
 */

const STORAGE_KEY = 'gate33_cookie_consent';
const CURRENT_VERSION = 1;
const CONSENT_EVENT = 'gate33:consent-updated';
const OPEN_PREF_EVENT = 'gate33:open-cookie-preferences';

export const DEFAULT_CHOICES = {
  essential: true, // always on
  analytics: false,
  marketing: false,
};

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

export const getConsent = () => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const getChoices = () => {
  const stored = getConsent();
  return stored?.choices || DEFAULT_CHOICES;
};

export const setConsent = (choices) => {
  if (!isBrowser()) return;
  const record = {
    version: CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    choices: { ...DEFAULT_CHOICES, ...choices, essential: true },
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record.choices }));
};

export const acceptAll = () => setConsent({ analytics: true, marketing: true });
export const rejectAll = () => setConsent({ analytics: false, marketing: false });

export const openCookiePreferences = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(OPEN_PREF_EVENT));
};

export const onConsentChange = (handler) => {
  if (!isBrowser()) return () => {};
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
};

export const onOpenPreferences = (handler) => {
  if (!isBrowser()) return () => {};
  window.addEventListener(OPEN_PREF_EVENT, handler);
  return () => window.removeEventListener(OPEN_PREF_EVENT, handler);
};

/**
 * Injects a <script> tag lazily. Idempotent (only one injection per src).
 */
const injectedScripts = new Set();
export const loadScript = (id, src, attrs = {}) => {
  if (!isBrowser() || injectedScripts.has(id)) return;
  injectedScripts.add(id);
  const s = document.createElement('script');
  s.id = id;
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
};

export const runInlineScript = (id, code) => {
  if (!isBrowser() || document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.text = code;
  document.head.appendChild(s);
};

/**
 * Loads / unloads analytics + marketing providers based on choices.
 * Call this on mount and on every consent change.
 */
export const applyConsent = (choices) => {
  if (!isBrowser()) return;
  const c = choices || getChoices();

  // --- Google Analytics 4 ---
  if (c.analytics) {
    const GA_ID = process.env.REACT_APP_GA_ID || 'G-QQDBQNDKX8';
    loadScript('gate33-ga4', `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
    runInlineScript(
      'gate33-ga4-init',
      `window.dataLayer = window.dataLayer || [];
       function gtag(){dataLayer.push(arguments);}
       window.gtag = gtag;
       gtag('js', new Date());
       gtag('config', '${GA_ID}', { anonymize_ip: true });`
    );
  }

  // --- LinkedIn Insight Tag ---
  if (c.marketing) {
    const LI_ID = process.env.REACT_APP_LINKEDIN_PARTNER_ID || '9448538';
    runInlineScript(
      'gate33-lintrk-init',
      `_linkedin_partner_id = "${LI_ID}";
       window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
       window._linkedin_data_partner_ids.push(_linkedin_partner_id);
       (function(l) { if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])}; window.lintrk.q=[]}
       var s = document.getElementsByTagName("script")[0];
       var b = document.createElement("script");
       b.type = "text/javascript";b.async = true;
       b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
       s.parentNode.insertBefore(b, s);})(window.lintrk);`
    );

    // --- Meta Pixel ---
    const FB_ID = process.env.REACT_APP_META_PIXEL_ID || '972521872004162';
    runInlineScript(
      'gate33-meta-pixel',
      `!function(f,b,e,v,n,t,s)
       {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
       n.callMethod.apply(n,arguments):n.queue.push(arguments)};
       if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
       n.queue=[];t=b.createElement(e);t.async=!0;
       t.src=v;s=b.getElementsByTagName(e)[0];
       s.parentNode.insertBefore(t,s)}(window,document,'script',
       'https://connect.facebook.net/en_US/fbevents.js');
       fbq('init', '${FB_ID}');
       fbq('track', 'PageView');`
    );
  }
};
